import { useEffect, useState } from 'react';
import { useMapMode, type MapMode } from '@/store/mapMode';
import { useSettings } from '@/store/settings';
import { useToasts } from '@/store/toasts';

interface ModeSpec {
  value: MapMode;
  label: string;
  /** Operator-facing description with `<Key/>` slots, rendered into the strip. */
  hintParts: HintPart[];
}

type HintPart =
  | { kind: 'text'; v: string }
  | { kind: 'key'; v: string };

const MODES: ModeSpec[] = [
  {
    value: 'direct',
    label: 'GOTO',
    hintParts: [
      { kind: 'key', v: 'Click' },
      { kind: 'text', v: '= GOTO ·' },
      { kind: 'key', v: 'Ctrl+Click' },
      { kind: 'text', v: '= queue next ·' },
      { kind: 'key', v: 'Right-click' },
      { kind: 'text', v: '= menu' },
    ],
  },
  {
    value: 'waypoint',
    label: 'Waypoint',
    hintParts: [
      { kind: 'text', v: 'click map to add a waypoint ·' },
      { kind: 'key', v: 'Alt' },
      { kind: 'text', v: '+ click marker to delete' },
    ],
  },
  {
    value: 'manual',
    label: 'Manual',
    hintParts: [
      { kind: 'key', v: 'SPACE' },
      { kind: 'text', v: 'hold ·' },
      { kind: 'key', v: '↑↓←→' },
      { kind: 'text', v: 'translate ·' },
      { kind: 'key', v: 'WS' },
      { kind: 'text', v: 'alt ·' },
      { kind: 'key', v: 'AD' },
      { kind: 'text', v: 'yaw' },
    ],
  },
  {
    value: 'measure',
    label: 'Measure',
    hintParts: [
      { kind: 'key', v: 'Click' },
      { kind: 'text', v: '= distance from selected drone ·' },
      { kind: 'key', v: 'Right-click' },
      { kind: 'text', v: '= menu · never commands' },
    ],
  },
];

/**
 * Compact mode strip at the top of the map. One row: the current mode name
 * (clickable to cycle), what to do in this mode, and the keyboard chord to
 * switch. Everything an operator needs in a glance — no separate panel.
 */
export function MapModeBar() {
  const mode = useMapMode((s) => s.mode);
  const cycle = useMapMode((s) => s.cycle);
  // GOTO-mode idle guard: after `directIdleExitS` with NO activity (clicks,
  // pans, cursor movement — MapView reports them via touch()), fall back to
  // inert Measure. A countdown chip appears for the last `directIdleWarnS`.
  const [idleRemainS, setIdleRemainS] = useState<number | null>(null);
  useEffect(() => {
    if (mode !== 'direct') {
      setIdleRemainS(null);
      return;
    }
    useMapMode.getState().touch();
    const i = window.setInterval(() => {
      const s = useSettings.getState().map;
      const exitS = s.directIdleExitS ?? 60;
      if (exitS <= 0) {
        setIdleRemainS(null);
        return;
      }
      const warnS = s.directIdleWarnS ?? 30;
      const remain = exitS - (Date.now() - useMapMode.getState().activityTs) / 1000;
      if (remain <= 0) {
        useMapMode.getState().setMode('measure');
        useToasts.getState().push({
          tone: 'info',
          title: 'GOTO mode idle — switched to Measure',
          body: 'Map clicks are inert again. Ctrl+Shift+M to re-arm GOTO clicks.',
          durationMs: 6000,
        });
        return;
      }
      setIdleRemainS(remain <= warnS ? Math.ceil(remain) : null);
    }, 1000);
    return () => window.clearInterval(i);
  }, [mode]);

  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (!(e.ctrlKey && e.shiftKey)) return;
      if (e.key.toLowerCase() !== 'm') return;
      const t = e.target as HTMLElement | null;
      if (t && (t.tagName === 'INPUT' || t.tagName === 'TEXTAREA' || t.isContentEditable)) return;
      e.preventDefault();
      cycle();
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [cycle]);

  const active = MODES.find((m) => m.value === mode) ?? MODES[0]!;

  return (
    <div className="pointer-events-none absolute inset-x-0 top-3 z-10 flex justify-center px-3">
      <button
        type="button"
        onClick={cycle}
        title="Cycle mode (Ctrl+Shift+M)"
        className="pointer-events-auto flex max-w-full items-center gap-2 rounded-sm border border-border-default bg-base/85 px-3 py-1.5 font-mono text-[10.5px] text-fg-secondary shadow-[var(--shadow-pop)] backdrop-blur-md transition-colors hover:bg-base/95 sm:gap-3 sm:px-4"
      >
        <span
          className="font-semibold uppercase tracking-[0.16em]"
          style={{ color: 'var(--color-accent)' }}
        >
          {active.label}
        </span>
        {idleRemainS != null && (
          <span
            title="No activity — GOTO mode switches to Measure when this hits 0. Move the mouse over the map to stay."
            className="rounded border border-status-warn/50 bg-status-warn/10 px-1.5 py-[1px] font-mono text-[10px] tabular-nums text-status-warn"
          >
            → Measure in {idleRemainS} s
          </span>
        )}
        <span className="hidden h-3 w-px bg-border-default sm:inline" aria-hidden />
        <span className="hidden flex-wrap items-center gap-1 text-fg-secondary sm:flex">
          {active.hintParts.map((p, i) =>
            p.kind === 'key' ? <Key key={i}>{p.v}</Key> : <span key={i}>{p.v}</span>,
          )}
        </span>
        <span className="hidden h-3 w-px bg-border-default md:inline" aria-hidden />
        <span className="hidden items-center gap-1 text-fg-muted md:flex">
          <Key>⌃⇧M</Key>
          <span>to cycle</span>
        </span>
      </button>
    </div>
  );
}

function Key({ children }: { children: React.ReactNode }) {
  return (
    <kbd className="inline-block rounded border border-border-default bg-surface-2 px-1.5 py-[1px] text-[10px] text-fg-primary">
      {children}
    </kbd>
  );
}
